import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import { LogIn, Mail, Lock, AlertCircle, Loader2, UserPlus, User, CheckCircle } from 'lucide-react';
import Card from './UI/Card';
// Logo desde carpeta public
const logoSelger = '/images/logojj.jpg';

const Login: React.FC = () => {
  const { loginWithEmail, register } = useAuth();
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState('');
  // Inicializar success desde sessionStorage si existe (para persistir entre hot-reloads)
  const [success, setSuccess] = useState(() => {
    const saved = sessionStorage.getItem('registro_exitoso');
    return saved || '';
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Ref para rastrear si el registro fue exitoso y evitar que se limpie el estado
  const registroExitosoRef = useRef(false);

  // Sincronizar success con sessionStorage
  useEffect(() => {
    if (success && success.trim() !== '') {
      sessionStorage.setItem('registro_exitoso', success);
    } else {
      // NO limpiar sessionStorage si hay un registro exitoso pendiente
      // Solo limpiar si realmente no hay mensaje Y no hay registro exitoso
      if (!registroExitosoRef.current) {
        sessionStorage.removeItem('registro_exitoso');
      }
    }
  }, [success]);
  
  // Restaurar success desde sessionStorage si se perdió (por ejemplo, después de signOut)
  useEffect(() => {
    if (!success && registroExitosoRef.current) {
      const savedSuccess = sessionStorage.getItem('registro_exitoso');
      if (savedSuccess) {
        setSuccess(savedSuccess);
      }
    }
  }, [success]);

  const handleSubmit = async (e?: React.FormEvent) => {
    // Prevenir comportamiento por defecto del formulario si existe el evento
    if (e) {
      e.preventDefault();
    }
    
    // Prevenir múltiples submits simultáneos
    if (loading) {
      return;
    }
    
    // NO limpiar success si ya existe un registro exitoso (para que el mensaje persista)
    // Solo limpiar error al inicio de un nuevo submit
    setError('');
    
    // Solo limpiar success si NO hay un registro exitoso pendiente
    if (!registroExitosoRef.current) {
      setSuccess('');
    }
    
    setLoading(true);

    try {
      if (isRegisterMode) {
        // Validar que las contraseñas coincidan
        if (password !== confirmPassword) {
          setError('Las contraseñas no coinciden.');
          setLoading(false);
          return;
        }

        // Validar contraseña
        if (!validatePassword(password)) {
          setError('La contraseña no cumple con los requisitos de seguridad.');
          setLoading(false);
          return;
        }

        // Validar nombre
        if (!nombre.trim()) {
          setError('El nombre es requerido.');
          setLoading(false);
          return;
        }

        // Registrar usuario
        const result = await register(email, password, nombre.trim());
        
        // Detener el loading primero
        setLoading(false);
        
        if (result.success) {
          // Marcar que el registro fue exitoso
          registroExitosoRef.current = true;
          
          // ✅ NUEVO: Mensaje de éxito claro y específico
          const successMessage = '✅ ¡Solicitud recibida! Hemos recibido tu solicitud para crear tu perfil. Espera a que el administrador te acepte.';
          
          // Limpiar errores y establecer éxito PRIMERO
          setError('');
          
          // IMPORTANTE: Establecer success de forma síncrona y guardar en sessionStorage inmediatamente
          // Hacer esto ANTES de cualquier operación asíncrona
          setSuccess(successMessage);
          sessionStorage.setItem('registro_exitoso', successMessage);
          registroExitosoRef.current = true;
          
          // Forzar un render inmediato
          await new Promise(resolve => setTimeout(resolve, 0));
          
          // IMPORTANTE: Cerrar sesión después de establecer el mensaje
          // para evitar auto-login y forzar que el usuario espere aprobación
          // Usar setTimeout para que no bloquee el render del mensaje
          setTimeout(async () => {
            try {
              await supabase.auth.signOut();
              
              // Después de cerrar sesión, restaurar el mensaje desde sessionStorage
              const savedSuccess = sessionStorage.getItem('registro_exitoso');
              if (savedSuccess) {
                // Usar setTimeout para asegurar que se ejecute después del re-render
                setTimeout(() => {
                  setSuccess(savedSuccess);
                }, 50);
              }
            } catch (signOutError) {
              // Error no crítico, solo loguear en desarrollo
              if (process.env.NODE_ENV === 'development') {
                console.warn('Error al cerrar sesión:', signOutError);
              }
            }
          }, 200);
          
          // Limpiar formulario
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          setNombre('');
          
          // Cambiar a modo login después de 10 segundos (dar más tiempo para leer)
          setTimeout(() => {
            registroExitosoRef.current = false;
            sessionStorage.removeItem('registro_exitoso');
            setIsRegisterMode(false);
            setSuccess('');
          }, 10000);
          
          // IMPORTANTE: Retornar aquí para evitar que el bloque catch limpie el estado
          return;
        } else {
          // Mostrar error
          setError(result.error || 'Error al crear la cuenta. Intenta nuevamente.');
          setSuccess(''); // Limpiar mensaje de éxito si hay error
        }
      } else {
        // Login
        try {
          // ✅ DEBUG: Log cuando se llama a loginWithEmail desde el componente Login
          console.log('🔍 [Login Component] ========================================');
          console.log('🔍 [Login Component] handleSubmit llamado para LOGIN');
          console.log('🔍 [Login Component] Email:', email);
          console.log('🔍 [Login Component] Timestamp:', new Date().toISOString());
          console.trace('🔍 [Login Component] Llamada desde:');
          console.log('🔍 [Login Component] ========================================');
          
          const success = await loginWithEmail(email, password);
          if (!success) {
            setError('Credenciales incorrectas. Verifica tu email y contraseña.');
          }
        } catch (err: any) {
          // Capturar errores específicos
          if (err.message && err.message.includes('pendiente')) {
            setError(err.message);
          } else if (err.message && (err.message.includes('conexión') || err.message.includes('Timeout') || err.message.includes('timeout'))) {
            // Error de conectividad/timeout
            setError(err.message || 'Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.');
          } else {
            setError(err.message || 'Credenciales incorrectas. Verifica tu email y contraseña.');
          }
        }
      }
    } catch (err) {
      console.error('Error en handleSubmit:', err);
      setLoading(false);
      
      // Solo establecer error si el registro NO fue exitoso
      // (para no sobrescribir un mensaje de éxito si el registro fue exitoso)
      if (!registroExitosoRef.current) {
        setError(isRegisterMode 
          ? 'Error al crear la cuenta. Intenta nuevamente.' 
          : 'Error al iniciar sesión. Intenta nuevamente.');
        setSuccess(''); // Solo limpiar success si realmente hubo un error
      }
    } finally {
      // Solo establecer loading a false si no fue un registro exitoso
      // (el registro exitoso ya lo maneja dentro del if)
      if (!isRegisterMode || !loading) {
        // Solo ejecutar si no estamos en modo registro o si loading ya fue manejado
      }
    }
  };

  const validatePassword = (pwd: string): boolean => {
    // Mínimo 12 caracteres, al menos una mayúscula, una minúscula, un número y un carácter especial
    const minLength = pwd.length >= 12;
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    return minLength && hasUpper && hasLower && hasNumber && hasSpecial;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo y Título */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img 
              src={logoSelger} 
              alt="J&J Operations Logo" 
              className="h-20 w-auto object-contain"
              onError={(e) => {
                // Fallback si la imagen no se carga
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            J&J Operations
          </h1>
          <p className="text-gray-600">
            Plataforma de Gestión De Gastos
          </p>
        </div>

        {/* Card de Login/Registro */}
        <Card className="shadow-2xl">
          <div className="p-8">
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  {isRegisterMode ? (
                    <UserPlus className="w-6 h-6 text-blue-600" />
                  ) : (
                    <LogIn className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {isRegisterMode ? 'Crear Perfil' : 'Iniciar Sesión'}
                </h2>
              </div>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                // Prevenir submit automático del formulario
                // Solo permitir submit mediante click en el botón
              }} 
              className="space-y-6"
            >
              {/* Success Message - Mostrar al inicio del formulario si existe */}
              {success && success.trim() !== '' ? (
                <div className="flex items-start gap-3 p-4 bg-green-100 border-2 border-green-500 rounded-lg text-green-900 shadow-lg">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 text-green-700 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold leading-relaxed">{success}</p>
                  </div>
                </div>
              ) : null}

              {/* Error Message - Mostrar si hay error y no hay mensaje de éxito */}
              {error && !success && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Nombre (solo en registro) */}
              {isRegisterMode && (
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="nombre"
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      required
                      autoComplete="name"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Juan Pérez"
                      disabled={loading}
                    />
                  </div>
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete={isRegisterMode ? "username" : "email"}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="usuario@jjingenieria.cl"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña {isRegisterMode && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
                    className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      isRegisterMode && password.length > 0 && !validatePassword(password) 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Ingresa tu contraseña"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>

                {/* Requisitos de contraseña - Siempre visible en modo registro */}
                {isRegisterMode && (
                  <div className={`mt-2 p-3 rounded-lg border ${
                    password.length > 0 && !validatePassword(password)
                      ? 'bg-red-50 border-red-200'
                      : password.length > 0 && validatePassword(password)
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <p className="text-xs font-semibold text-gray-700 mb-2">
                      {password.length > 0 && validatePassword(password) 
                        ? '✓ Contraseña segura' 
                        : 'La contraseña debe cumplir con los siguientes requisitos:'}
                    </p>
                    <ul className="text-xs space-y-1">
                      <li className={`flex items-center gap-2 ${
                        password.length >= 12 ? 'text-green-600 font-medium' : 'text-gray-600'
                      }`}>
                        {password.length >= 12 ? '✓' : '○'} Mínimo 12 caracteres
                      </li>
                      <li className={`flex items-center gap-2 ${
                        /[A-Z]/.test(password) ? 'text-green-600 font-medium' : 'text-gray-600'
                      }`}>
                        {/[A-Z]/.test(password) ? '✓' : '○'} Al menos una mayúscula (A-Z)
                      </li>
                      <li className={`flex items-center gap-2 ${
                        /[a-z]/.test(password) ? 'text-green-600 font-medium' : 'text-gray-600'
                      }`}>
                        {/[a-z]/.test(password) ? '✓' : '○'} Al menos una minúscula (a-z)
                      </li>
                      <li className={`flex items-center gap-2 ${
                        /[0-9]/.test(password) ? 'text-green-600 font-medium' : 'text-gray-600'
                      }`}>
                        {/[0-9]/.test(password) ? '✓' : '○'} Al menos un número (0-9)
                      </li>
                      <li className={`flex items-center gap-2 ${
                        /[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-600 font-medium' : 'text-gray-600'
                      }`}>
                        {/[!@#$%^&*(),.?":{}|<>]/.test(password) ? '✓' : '○'} Al menos un carácter especial (!@#$%^&*...)
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Confirmar Contraseña (solo en registro) */}
              {isRegisterMode && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Confirma tu contraseña"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && password !== confirmPassword && (
                    <p className="mt-2 text-xs text-red-600">Las contraseñas no coinciden</p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  loading || 
                  !email || 
                  !password || 
                  (isRegisterMode && (!nombre || !confirmPassword || password !== confirmPassword || !validatePassword(password)))
                }
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{isRegisterMode ? 'Creando cuenta...' : 'Iniciando sesión...'}</span>
                  </>
                ) : (
                  <>
                    {isRegisterMode ? (
                      <>
                        <UserPlus className="w-5 h-5" />
                        <span>Crear Perfil</span>
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5" />
                        <span>Iniciar Sesión</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </form>

            {/* Botón para cambiar entre Login y Registro */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  // Solo limpiar success si NO hay un registro exitoso pendiente
                  if (!registroExitosoRef.current) {
                    setSuccess('');
                  }
                  setIsRegisterMode(!isRegisterMode);
                  setError('');
                  setEmail('');
                  setPassword('');
                  setConfirmPassword('');
                  setNombre('');
                }}
                className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-2 transition-colors py-2"
              >
                {isRegisterMode ? (
                  <>
                    <User className="w-4 h-4" />
                    Ya tengo cuenta
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Crear perfil
                  </>
                )}
              </button>
            </div>

            {/* Información adicional */}
            {isRegisterMode && (
              <div className="mt-4">
                <p className="text-xs text-center text-gray-500">
                  Al crear tu perfil, aceptas los términos y condiciones de uso de la plataforma.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;

